import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { API } from "@/auth";
import useSeo from "@/seo";

function displayDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso || "";
}

const PAGE = 50;

export default function PreviousResultsPublic() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE);
  const nav = useNavigate();

  useSeo({
    title: "Previous Results | Morning Sunday Teer",
    description: "Complete history of Shillong Morning Teer previous results — First Round and Second Round numbers by date.",
    path: "/previous-results",
  });

  useEffect(() => {
    document.title = "Previous Results | Morning Sunday Teer";
    axios.get(`${API}/results`).then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const disp = displayDate(r.date).toLowerCase();
      return (
        disp.includes(q) ||
        (r.date || "").toLowerCase().includes(q) ||
        (r.first_round || "").includes(q) ||
        (r.second_round || "").includes(q)
      );
    });
  }, [rows, query]);

  const shown = filtered.slice(0, visible);

  return (
    <div className="pr-shell" data-testid="previous-results-page">
      <div className="pr-container">
        <button
          className="pr-back"
          data-testid="pr-back-button"
          onClick={() => nav("/")}
        >
          <ArrowLeft /> Back
        </button>

        <h1 className="pr-title" data-testid="pr-heading">Morning Teer Previous Results</h1>
        <p className="pr-sub">Complete history of Shillong Morning Teer results</p>

        <div className="pr-search">
          <Search size={18} />
          <input
            data-testid="pr-search-input"
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setVisible(PAGE); }}
            placeholder="Search by date (e.g. 08/2026)"
            aria-label="Search results"
          />
        </div>

        <div className="pr-card">
          <div className="pr-head" data-testid="pr-table-head">
            <div>DATE</div>
            <div>F/R (10:30 AM)</div>
            <div>S/R (11:30 AM)</div>
          </div>
          {loading ? (
            <div className="pr-empty" data-testid="pr-loading">Loading…</div>
          ) : shown.length === 0 ? (
            <div className="pr-empty" data-testid="pr-empty">No results found.</div>
          ) : (
            shown.map((r) => (
              <div className="pr-row" key={r.id} data-testid={`pr-row-${r.id}`}>
                <div className="pr-date">{displayDate(r.date)}</div>
                <div className="pr-cell"><span className="pr-num" data-testid={`pr-fr-${r.id}`}>{r.first_round || "--"}</span></div>
                <div className="pr-cell"><span className="pr-num" data-testid={`pr-sr-${r.id}`}>{r.second_round || "--"}</span></div>
              </div>
            ))
          )}
        </div>

        {!loading && filtered.length > visible && (
          <div className="pr-more">
            <button
              className="pr-more-btn"
              data-testid="pr-load-more"
              onClick={() => setVisible((v) => v + PAGE)}
            >
              Load more ({filtered.length - visible} left)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
